import React, { useEffect, useState } from "react";
import {
  Plus,
  Search,
  MapPin,
  Phone,
  X,
  RefreshCw,
  Building2,
  Clock,
  Eye,
  Calendar,
  Pencil,
  Trash2,
  Save,
  KeyRound,
  ExternalLink,
  Power,
} from "lucide-react";
import api, { ApiResponse } from "../../services/api";
import { getTokenInfo } from "../../utils/auth";
import {
  getLocationIntegrations,
  updateLocationIntegration,
  updateHotelStatus,
} from "../../services/api";
import MapWithSearch from "../../components/MapWithSearch";
import { toast } from "react-hot-toast";
import { useAuth } from "../../context/AuthContext";
interface TimeSlot {
  start: string;
  end: string;
}

interface DaySchedule {
  day: string;
  isOpen: boolean;
  slots: TimeSlot[];
}

interface Hotel {
  _id: string;
  hotelName: string;
  address: string;
  phoneNo: string;
  latitude: number;
  longitude: number;
  country: string;
  state: string;
  city: string;
  defaultTip?: boolean;
  cashEnabled?: boolean;
  deliveryTipToPartner?: boolean;
  baseTipPercent?: number;
  tipGapPercent?: number;
  withinRadius?: number;
  minOrderAmount?: number;
  deliveryAmount?: number;
  asapDelay?: number;
  storeTimeline?: DaySchedule[];
  pickupTimeline?: DaySchedule[];
  deliveryTimeline?: DaySchedule[];
  isActive?: boolean;
}


const HotelManagement: React.FC = () => {
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const [showIntegrationsModal, setShowIntegrationsModal] = useState(false);
  const [integrationsHotel, setIntegrationsHotel] = useState<Hotel | null>(
    null,
  );
  const [integrationsLoading, setIntegrationsLoading] = useState(false);
  const [integrationsSaving, setIntegrationsSaving] = useState<string | null>(
    null,
  );
  const [integrationsStatusMap, setIntegrationsStatusMap] = useState<
    Record<string, any>
  >({});
  const [integrationsForms, setIntegrationsForms] = useState<
    Record<
      string,
      { accountEmail: string; credentials: Record<string, string> }
    >
  >({});
  const [selectedHotel, setSelectedHotel] = useState<Hotel | null>(null);
  const [editingHotel, setEditingHotel] = useState<Hotel | null>(null);
  const [togglingStatusId, setTogglingStatusId] = useState<string | null>(
    null,
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(9);
  const { user } = useAuth();


  const tokenInfo = getTokenInfo();
  const adminRole = tokenInfo?.role || "";
  // Location admins can view their location but only edit its timelines
  const isMasterAdmin = adminRole === "master_admin";

  // Form states
  const [hotelName, setHotelName] = useState("");
  const [phoneNo, setPhoneNo] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("");
  const [flatBlock, setFlatBlock] = useState("");
  const [area, setArea] = useState("");
  const [latitude, setLatitude] = useState(0);
  const [longitude, setLongitude] = useState(0);
  const [fullMapAddress, setFullMapAddress] = useState("");
  const [formLoading, setFormLoading] = useState(false);
  const [mapResetKey, setMapResetKey] = useState(0);
  const [defaultTip, setdefaultTip] = useState(true);
  const [cashEnabled, setCashEnabled] = useState(false);
  const [deliveryTipToPartner, setDeliveryTipToPartner] = useState(false);
  const [baseTipPercent, setBaseTipPercent] = useState<number | string>("");
  const [tipGapPercent, setTipGapPercent] = useState<number | string>("");
  const [withinRadius, setWithinRadius] = useState<number | string>("");
  const [minOrderAmount, setMinOrderAmount] = useState<number | string>("");
  const [deliveryAmount, setDeliveryAmount] = useState<number | string>("");
  const [asapDelay, setAsapDelay] = useState<number | string>("");
  const [originalLocation, setOriginalLocation] = useState<{
    fullMapAddress: string;
    latitude: number;
    longitude: number;
    city: string;
    state: string;
    country: string;
    flatBlock: string;
    area: string;
  } | null>(null);

  // Timeline states
  const [storeTimeline, setStoreTimeline] = useState<DaySchedule[]>([]);
  const [pickupTimeline, setPickupTimeline] = useState<DaySchedule[]>([]);
  const [deliveryTimeline, setDeliveryTimeline] = useState<DaySchedule[]>([]);
  const [activeTimelineTab, setActiveTimelineTab] = useState<
    "store" | "pickup" | "delivery"
  >("store");

  // Convert Google Places opening hours to our DaySchedule format
  const parseOpeningHoursToTimeline = (openingHours: any): DaySchedule[] => {
    const dayNames = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];

    // Initialize all days as closed
    const timeline: DaySchedule[] = dayNames.map((day) => ({
      day,
      isOpen: false,
      slots: [],
    }));

    if (openingHours?.periods) {
      // Group periods by day
      const periodsByDay: { [key: number]: { start: string; end: string }[] } =
        {};

      openingHours.periods.forEach((period: any) => {
        const dayIndex = period.open?.day;
        if (dayIndex === undefined) return;

        if (!periodsByDay[dayIndex]) {
          periodsByDay[dayIndex] = [];
        }

        // Convert HHMM format to HH:MM
        const openTime = period.open?.time || "0000";
        const closeTime = period.close?.time || "2359";

        const formatTime = (time: string) => {
          const hours = time.substring(0, 2);
          const minutes = time.substring(2, 4);
          return `${hours}:${minutes}`;
        };

        periodsByDay[dayIndex].push({
          start: formatTime(openTime),
          end: formatTime(closeTime),
        });
      });

      // Update timeline with the periods
      Object.entries(periodsByDay).forEach(([dayIndexStr, slots]) => {
        const dayIndex = parseInt(dayIndexStr);
        if (dayIndex >= 0 && dayIndex < 7) {
          timeline[dayIndex].isOpen = true;
          timeline[dayIndex].slots = slots;
        }
      });
    } else if (openingHours?.weekday_text) {
      // Parse from weekday_text if periods not available
      // Format: "Monday: 9:00 AM – 9:00 PM" or "Monday: Closed"
      openingHours.weekday_text.forEach((text: string, index: number) => {
        // weekday_text starts from Monday (index 0), but our dayNames starts from Sunday
        const dayIndex = (index + 1) % 7; // Convert to Sunday = 0 format

        if (text.toLowerCase().includes("closed")) {
          timeline[dayIndex].isOpen = false;
          timeline[dayIndex].slots = [];
        } else {
          // Parse time ranges like "9:00 AM – 9:00 PM"
          const timeMatch = text.match(
            /(\d{1,2}:\d{2}\s*(?:AM|PM)?)\s*[–-]\s*(\d{1,2}:\d{2}\s*(?:AM|PM)?)/i,
          );
          if (timeMatch) {
            const convertTo24Hour = (time12: string): string => {
              const [timePart, period] = time12.trim().split(/\s+/);
              let [hours, minutes] = timePart.split(":").map(Number);

              if (period?.toUpperCase() === "PM" && hours !== 12) {
                hours += 12;
              } else if (period?.toUpperCase() === "AM" && hours === 12) {
                hours = 0;
              }

              return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
            };

            timeline[dayIndex].isOpen = true;
            timeline[dayIndex].slots = [
              {
                start: convertTo24Hour(timeMatch[1]),
                end: convertTo24Hour(timeMatch[2]),
              },
            ];
          }
        }
      });
    }

    // Reorder to start from Monday (our UI expects Monday first)
    const mondayFirst = [
      timeline[1], // Monday
      timeline[2], // Tuesday
      timeline[3], // Wednesday
      timeline[4], // Thursday
      timeline[5], // Friday
      timeline[6], // Saturday
      timeline[0], // Sunday
    ];

    return mondayFirst;
  };

  const handleLocationChange = (data: any) => {
    console.log("Received data in parent:", data);

    if (data.fullAddress) setFullMapAddress(data.fullAddress);
    if (data.lat) setLatitude(data.lat);
    if (data.lng) setLongitude(data.lng);
    if (data.city) setCity(data.city);
    if (data.state) setState(data.state);
    if (data.country) setCountry(data.country);
    if (data.flatBlock) setFlatBlock(data.flatBlock);
    if (data.area) setArea(data.area);

    // Only prefill name and phone if not editing an existing hotel
    if (!editingHotel) {
      // Prefill Location Name if available
      if (data.placeName) {
        setHotelName(data.placeName);
      }

      // Prefill Phone Number if available
      if (data.phoneNumber) {
        // Clean the phone number - remove all non-digits
        let cleaned = data.phoneNumber.replace(/\D/g, "");

        // Remove leading country code if present
        // US numbers: if 11 digits starting with 1, remove the leading 1
        if (cleaned.length === 11 && cleaned.startsWith("1")) {
          cleaned = cleaned.substring(1);
        }
        // For other countries with different codes, just take last 10 digits
        else if (cleaned.length > 10) {
          cleaned = cleaned.slice(-10);
        }

        setPhoneNo(formatPhone(cleaned));
      }

      // If opening hours are provided, prefill the timelines
      if (data.openingHours) {
        console.log("Parsing opening hours:", data.openingHours);
        const parsedTimeline = parseOpeningHoursToTimeline(data.openingHours);
        console.log("Parsed timeline:", parsedTimeline);

        setStoreTimeline(parsedTimeline);
        // Use same hours for pickup and delivery by default
        setPickupTimeline(
          parsedTimeline.map((day) => ({ ...day, slots: [...day.slots] })),
        );
        setDeliveryTimeline(
          parsedTimeline.map((day) => ({ ...day, slots: [...day.slots] })),
        );
      }
    }
  };

  const formatPhone = (phoneNumber: string): string => {
    const cleaned = phoneNumber.replace(/\D/g, "");

    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    }
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const setPhoneNumber = (number: string) => {
    const formatted = formatPhone(number);
    setPhoneNo(formatted);
  };

  useEffect(() => {
    fetchHotels();
    initializeTimelines();
  }, []);

  const initializeTimelines = () => {
    const days = [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ];
    const defaultSchedule = days.map((day) => ({
      day,
      isOpen: day !== "Sunday", // Default: closed on Sunday
      slots: day !== "Sunday" ? [{ start: "09:00", end: "21:00" }] : [],
    }));

    setStoreTimeline([...defaultSchedule]);

    // Different default for pickup (with split hours)
    const pickupSchedule = days.map((day) => ({
      day,
      isOpen: true, // Pickup available all days by default
      slots: [
        { start: "08:00", end: "14:00" },
        { start: "17:00", end: "23:00" },
      ],
    }));
    setPickupTimeline([...pickupSchedule]);

    // Default for delivery
    const deliverySchedule = days.map((day) => ({
      day,
      isOpen: true,
      slots: [{ start: "10:00", end: "22:00" }],
    }));
    setDeliveryTimeline([...deliverySchedule]);
  };

  const fetchHotels = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<ApiResponse<Hotel[]>>(
        "/admin/getHotelInformation",
      );
      setHotels(res.data.data);
    } catch (err) {
      setError("Failed to load hotels. Please try again.");
      setHotels([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!hotelName.trim()) {
      toast.error("Please enter hotel name");
      return;
    }
    if (!phoneNo.trim()) {
      toast.error("Please enter phone number");
      return;
    }
    if (!latitude || !longitude) {
      toast.error("Please select location on map");
      return;
    }

    setFormLoading(true);
    setError(null);

    const payload = !isMasterAdmin
      ? {
          storeTimeline,
          pickupTimeline,
          deliveryTimeline,
          asapDelay: asapDelay === "" ? 0 : Number(asapDelay),
        }
      : {
          hotelName: hotelName.trim(),
          phoneNo: phoneNo.trim().replace(/\D/g, ""),
          address: fullMapAddress,
          latitude,
          longitude,
          city,
          state,
          country,
          defaultTip,
          cashEnabled,
          deliveryTipToPartner,
          baseTipPercent: baseTipPercent === "" ? null : Number(baseTipPercent),
          tipGapPercent: tipGapPercent === "" ? null : Number(tipGapPercent),
          withinRadius: withinRadius === "" ? null : Number(withinRadius),
          minOrderAmount: minOrderAmount === "" ? null : Number(minOrderAmount),
          deliveryAmount: deliveryAmount === "" ? null : Number(deliveryAmount),
          asapDelay: asapDelay === "" ? 0 : Number(asapDelay),
          storeTimeline,
          pickupTimeline,
          deliveryTimeline,
        };

    try {
      if (editingHotel) {
        await api.put(`/admin/hotels/${editingHotel._id}`, payload);
        toast.success("Hotel updated successfully!");
      } else {
        await api.post("/admin/addHotel", payload);
        toast.success("Hotel added successfully!");
      }

      resetForm();
      setShowModal(false);
      fetchHotels();
    } catch (err) {
      const action = editingHotel ? "update" : "add";
      setError(`Failed to ${action} hotel. Please try again.`);
      toast.error(`Failed to ${action} hotel.`);
    } finally {
      setFormLoading(false);
    }
  };

  const resetForm = () => {
    setHotelName("");
    setPhoneNo("");
    setFullMapAddress("");
    setLatitude(0);
    setLongitude(0);
    setCity("");
    setState("");
    setCountry("");
    setFlatBlock("");
    setArea("");
    setActiveTimelineTab("store");
    initializeTimelines();
    setEditingHotel(null);
    setOriginalLocation(null);
    setdefaultTip(true);
    setCashEnabled(false);
    setDeliveryTipToPartner(false);
    setBaseTipPercent("");
    setTipGapPercent("");
    setWithinRadius("");
    setMinOrderAmount("");
    setDeliveryAmount("");
    setAsapDelay("");
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const handleEdit = (hotel: Hotel) => {
    setEditingHotel(hotel);
    setHotelName(hotel.hotelName);
    setPhoneNo(hotel.phoneNo);
    setFullMapAddress(hotel.address);
    setLatitude(hotel.latitude);
    setLongitude(hotel.longitude);
    setCity(hotel.city);
    setState(hotel.state);
    setCountry(hotel.country);
    setOriginalLocation({
      fullMapAddress: hotel.address,
      latitude: hotel.latitude,
      longitude: hotel.longitude,
      city: hotel.city,
      state: hotel.state,
      country: hotel.country,
      flatBlock: "",
      area: "",
    });
    if (hotel.storeTimeline) setStoreTimeline(hotel.storeTimeline);
    if (hotel.pickupTimeline) setPickupTimeline(hotel.pickupTimeline);
    if (hotel.deliveryTimeline) setDeliveryTimeline(hotel.deliveryTimeline);
    setActiveTimelineTab("store");
    setdefaultTip(hotel.defaultTip ?? true);
    setCashEnabled(hotel.cashEnabled ?? false);
    setDeliveryTipToPartner(hotel.deliveryTipToPartner ?? false);
    setBaseTipPercent(hotel.baseTipPercent ?? "");
    setTipGapPercent(hotel.tipGapPercent ?? "");
    setWithinRadius(hotel.withinRadius ?? "");
    setMinOrderAmount(hotel.minOrderAmount ?? "");
    setDeliveryAmount(hotel.deliveryAmount ?? "");
    setAsapDelay(hotel.asapDelay ?? "");
    setShowModal(true);
  };

  const handleResetLocation = () => {
    if (!originalLocation) return;
    setFullMapAddress(originalLocation.fullMapAddress);
    setLatitude(originalLocation.latitude);
    setLongitude(originalLocation.longitude);
    setCity(originalLocation.city);
    setState(originalLocation.state);
    setCountry(originalLocation.country);
    setFlatBlock(originalLocation.flatBlock);
    setArea(originalLocation.area);
    setMapResetKey((key) => key + 1);
  };

  const hasLocationChanges =
    !!editingHotel &&
    !!originalLocation &&
    (latitude !== originalLocation.latitude ||
      longitude !== originalLocation.longitude ||
      fullMapAddress !== originalLocation.fullMapAddress ||
      city !== originalLocation.city ||
      state !== originalLocation.state ||
      country !== originalLocation.country);

  const handleDelete = async (hotel: Hotel) => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${hotel.hotelName}"? This action cannot be undone.`,
      )
    ) {
      return;
    }

    try {
      await api.delete(`/admin/hotels/${hotel._id}`);
      toast.success("Hotel deleted successfully!");
      fetchHotels();
    } catch (err) {
      toast.error("Failed to delete hotel.");
    }
  };

  const handleToggleStatus = async (hotel: Hotel) => {
    const nextIsActive = !(hotel.isActive ?? true);
    const action = nextIsActive ? "enable" : "disable";

    if (
      !window.confirm(
        `Are you sure you want to ${action} "${hotel.hotelName}"?`,
      )
    ) {
      return;
    }

    setTogglingStatusId(hotel._id);
    try {
      await updateHotelStatus(hotel._id, nextIsActive);
      toast.success(`Location ${nextIsActive ? "enabled" : "disabled"} successfully!`);
      fetchHotels();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.error || `Failed to ${action} location.`,
      );
    } finally {
      setTogglingStatusId(null);
    }
  };

  const openTimelineModal = (hotel: Hotel) => {
    setSelectedHotel(hotel);
    setShowTimelineModal(true);
  };


  const LOCATION_PROVIDERS = [
    {
      key: "uberDirect" as const,
      title: "Uber Direct",
      docsUrl: "https://developer.uber.com/docs/deliveries/overview",
      signupUrl: "https://developer.uber.com/",
      accountHelp: "Uber developer account email for this location.",
      fields: [
        { key: "UBER_DIRECT_CLIENT_ID", label: "Client ID", required: true },
        {
          key: "UBER_DIRECT_CLIENT_SECRET",
          label: "Client Secret",
          required: true,
        },
        {
          key: "UBER_DIRECT_CUSTOMER_ID",
          label: "Customer ID (optional)",
          required: false,
        },
        {
          key: "UBER_WEBHOOK_SECRET",
          label: "Webhook Secret (optional)",
          required: false,
        },
      ],
    },
    {
      key: "doordashDeveloper" as const,
      title: "DoorDash Developer",
      docsUrl: "https://developer.doordash.com/en-US/docs/drive",
      signupUrl: "https://developer.doordash.com/",
      accountHelp: "DoorDash developer organization email.",
      fields: [
        { key: "DOORDASH_DEVELOPER_ID", label: "Developer ID", required: true },
        { key: "DOORDASH_KEY_ID", label: "Key ID", required: true },
        {
          key: "DOORDASH_SIGNING_SECRET",
          label: "Signing Secret",
          required: true,
        },
        {
          key: "DOORDASH_API_BASE_URL",
          label: "API Base URL (optional)",
          required: false,
        },
        {
          key: "DOORDASH_BUSINESS_ID",
          label: "Business ID (optional)",
          required: false,
        },
      ],
    },
    {
      key: "square" as const,
      title: "Square",
      docsUrl: "https://developer.squareup.com/docs",
      signupUrl: "https://developer.squareup.com/",
      accountHelp: "Square developer account email for this location.",
      fields: [
        { key: "SQUARE_ACCESS_TOKEN", label: "Access Token", required: true },
        { key: "SQUARE_LOCATION_ID", label: "Location ID", required: true },
        {
          key: "SQUARE_APPLICATION_ID",
          label: "Application ID",
          required: true,
        },
        {
          key: "SQUARE_ENVIRONMENT",
          label: "Environment (sandbox/production)",
          required: true,
        },
        {
          key: "SQUARE_WEBHOOK_SIGNATURE_KEY",
          label: "Webhook Signature Key (optional)",
          required: false,
        },
      ],
    },
    // {
    //   key: "stripe" as const,
    //   title: "Stripe",
    //   docsUrl: "https://docs.stripe.com/",
    //   signupUrl: "https://dashboard.stripe.com/",
    //   accountHelp: "Stripe account email for this location.",
    //   fields: [
    //     { key: "STRIPE_SECRET_KEY", label: "Secret Key", required: true },
    //     {
    //       key: "WEBHOOK_ENDPOINT_SECRET",
    //       label: "Webhook Endpoint Secret (optional)",
    //       required: false,
    //     },
    //   ],
    // },
  ];

  const openIntegrationsModal = async (hotel: Hotel) => {
    setIntegrationsHotel(hotel);
    setShowIntegrationsModal(true);
    setIntegrationsLoading(true);
    const defaultForms: Record<
      string,
      { accountEmail: string; credentials: Record<string, string> }
    > = {};
    LOCATION_PROVIDERS.forEach((p) => {
      defaultForms[p.key] = { accountEmail: "", credentials: {} };
    });
    setIntegrationsForms(defaultForms);
    try {
      const res = await getLocationIntegrations(hotel._id);
      const list = res.data?.data || [];
      const nextStatusMap: Record<string, any> = {};
      const nextForms = { ...defaultForms };
      list.forEach((item: any) => {
        nextStatusMap[item.provider] = item;
        if (nextForms[item.provider]) {
          nextForms[item.provider].accountEmail = item.accountEmail || "";
        }
      });
      setIntegrationsStatusMap(nextStatusMap);
      setIntegrationsForms(nextForms);
    } catch {
      toast.error("Failed to load integration settings");
    } finally {
      setIntegrationsLoading(false);
    }
  };

  const saveLocationIntegration = async (
    provider: "uberDirect" | "doordashDeveloper" | "square" | "stripe",
  ) => {
    if (!integrationsHotel) return;
    setIntegrationsSaving(provider);
    try {
      await updateLocationIntegration(
        integrationsHotel._id,
        provider,
        integrationsForms[provider],
      );
      toast.success("Credentials saved securely");
      setIntegrationsForms((prev) => ({
        ...prev,
        [provider]: { ...prev[provider], credentials: {} },
      }));
      // Refresh status
      const res = await getLocationIntegrations(integrationsHotel._id);
      const list = res.data?.data || [];
      const nextStatusMap: Record<string, any> = {};
      list.forEach((item: any) => {
        nextStatusMap[item.provider] = item;
      });
      setIntegrationsStatusMap(nextStatusMap);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Failed to save credentials");
    } finally {
      setIntegrationsSaving(null);
    }
  };


  const formatTimeSlots = (slots: TimeSlot[]) => {
    return slots.map((slot) => `${slot.start} - ${slot.end}`).join(", ");
  };

  const updateTimelineDay = (
    timeline: DaySchedule[],
    dayIndex: number,
    updates: Partial<DaySchedule>,
    setTimeline: React.Dispatch<React.SetStateAction<DaySchedule[]>>,
  ) => {
    const newTimeline = [...timeline];
    newTimeline[dayIndex] = { ...newTimeline[dayIndex], ...updates };
    setTimeline(newTimeline);
  };

  const addTimeSlot = (
    timeline: DaySchedule[],
    dayIndex: number,
    setTimeline: React.Dispatch<React.SetStateAction<DaySchedule[]>>,
  ) => {
    const newTimeline = [...timeline];
    newTimeline[dayIndex].slots.push({ start: "09:00", end: "17:00" });
    setTimeline(newTimeline);
  };

  const removeTimeSlot = (
    timeline: DaySchedule[],
    dayIndex: number,
    slotIndex: number,
    setTimeline: React.Dispatch<React.SetStateAction<DaySchedule[]>>,
  ) => {
    const newTimeline = [...timeline];
    newTimeline[dayIndex].slots.splice(slotIndex, 1);
    setTimeline(newTimeline);
  };

  const updateTimeSlot = (
    timeline: DaySchedule[],
    dayIndex: number,
    slotIndex: number,
    field: "start" | "end",
    value: string,
    setTimeline: React.Dispatch<React.SetStateAction<DaySchedule[]>>,
  ) => {
    const newTimeline = [...timeline];
    newTimeline[dayIndex].slots[slotIndex][field] = value;
    setTimeline(newTimeline);
  };

  const getCurrentTimeline = () => {
    switch (activeTimelineTab) {
      case "store":
        return { timeline: storeTimeline, setTimeline: setStoreTimeline };
      case "pickup":
        return { timeline: pickupTimeline, setTimeline: setPickupTimeline };
      case "delivery":
        return { timeline: deliveryTimeline, setTimeline: setDeliveryTimeline };
    }
  };

  const renderTimelineEditor = () => {
    const { timeline, setTimeline } = getCurrentTimeline();

    return (
      <div className="space-y-4">
        {timeline.map((day, dayIndex) => (
          <div key={day.day} className="border rounded-lg bg-gray-50 p-3 sm:p-4">
            <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <span className="min-w-[88px] font-medium text-gray-700">
                  {day.day}
                </span>
                <label className="flex shrink-0 items-center gap-2">
                  <input
                    type="checkbox"
                    checked={day.isOpen}
                    onChange={(e) =>
                      updateTimelineDay(
                        timeline,
                        dayIndex,
                        {
                          isOpen: e.target.checked,
                          slots: e.target.checked
                            ? day.slots.length === 0
                              ? [{ start: "09:00", end: "17:00" }]
                              : day.slots
                            : [],
                        },
                        setTimeline,
                      )
                    }
                    className="rounded"
                  />
                  <span className="text-sm text-gray-600">Open</span>
                </label>
              </div>
              {day.isOpen && (
                <button
                  type="button"
                  onClick={() => addTimeSlot(timeline, dayIndex, setTimeline)}
                  className="w-full rounded bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 sm:w-auto sm:py-1"
                >
                  Add Slot
                </button>
              )}
            </div>

            {day.isOpen && (
              <div className="space-y-2 sm:ml-6">
                {day.slots.map((slot, slotIndex) => (
                  <div key={slotIndex} className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <input
                      type="time"
                      value={slot.start}
                      onChange={(e) =>
                        updateTimeSlot(
                          timeline,
                          dayIndex,
                          slotIndex,
                          "start",
                          e.target.value,
                          setTimeline,
                        )
                      }
                      className="w-full rounded border px-3 py-1 text-sm sm:w-auto"
                    />
                    <span className="text-center text-gray-500 sm:text-left">to</span>
                    <input
                      type="time"
                      value={slot.end}
                      onChange={(e) =>
                        updateTimeSlot(
                          timeline,
                          dayIndex,
                          slotIndex,
                          "end",
                          e.target.value,
                          setTimeline,
                        )
                      }
                      className="w-full rounded border px-3 py-1 text-sm sm:w-auto"
                    />
                    {day.slots.length > 1 && (
                      <button
                        type="button"
                        onClick={() =>
                          removeTimeSlot(
                            timeline,
                            dayIndex,
                            slotIndex,
                            setTimeline,
                          )
                        }
                        className="w-full rounded bg-red-600 px-2 py-1 text-sm text-white hover:bg-red-700 sm:w-auto"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderTimelineSection = (
    title: string,
    timeline: DaySchedule[] | undefined,
    color: string,
  ) => {
    if (!timeline) return null;

    return (
      <div className="mb-6">
        <h4 className={`text-lg font-semibold mb-3 ${color}`}>{title}</h4>
        <div className="grid grid-cols-1 gap-2">
          {timeline.map((day, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-3 border rounded-lg bg-gray-50"
            >
              <div className="flex items-center gap-3">
                <Calendar size={16} className="text-gray-500" />
                <span className="font-medium text-gray-700 w-20">
                  {day.day}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div
                  className={`w-2 h-2 rounded-full ${day.isOpen ? "bg-green-500" : "bg-red-500"}`}
                ></div>
                <span className="text-sm text-gray-600">
                  {day.isOpen
                    ? day.slots.length > 0
                      ? formatTimeSlots(day.slots)
                      : "Open (No specific hours)"
                    : "Closed"}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Filter and pagination logic
  const filteredHotels = hotels.filter(
    (hotel) =>
      (hotel.hotelName &&
        hotel.hotelName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (hotel.address &&
        hotel.address.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (hotel.city &&
        hotel.city.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (hotel.state &&
        hotel.state.toLowerCase().includes(searchTerm.toLowerCase())),
  );

  const totalPages = Math.ceil(filteredHotels.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedHotels = filteredHotels.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="relative max-w-7xl overflow-y-auto no-scrollbar mx-auto p-4 pt-0 lg:p-8 flex flex-col h-screen">
      {/* ================= HEADER ================= */}
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-sm py-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Your Locations</h1>

          {adminRole === "master_admin" && (
            <button
              onClick={openAddModal}
              className="w-auto self-start sm:self-auto px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition inline-flex items-center gap-2 whitespace-nowrap">
              <Plus size={18} />
              Add New Location
            </button>
          )}
        </div>

        {/* ================= SEARCH ================= */}
        <div className="rounded-lg mb-6">
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Search Your Location
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={18}
              />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search by name, address, city, or state..."
                className="pl-10 pr-4 py-2 w-full border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={fetchHotels}
              className="h-10 w-10 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition flex items-center justify-center"
              title="Refresh Location "
            >
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>
      </div>

      {/* ================= ERROR ================= */}
      {error && (
        <div className="bg-red-500 text-white p-3 rounded-md mb-4 text-center">
          {error}
        </div>
      )}

      {/* ================= CONTENT ================= */}
      <div className="flex-1">
        {paginatedHotels.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <p className="text-lg font-medium">No locations found</p>
              <p className="text-sm mt-1">
                {searchTerm
                  ? "Try searching with a different term"
                  : "Add your first location to get started"}
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {paginatedHotels.map((hotel) => (
              <div
                key={hotel._id}
                className="bg-white border rounded-lg shadow-sm hover:shadow-md p-2 transition-shadow h-full overflow-hidden flex flex-col"
              >
                {/* Hotel Header */}
                <div className="p-4 border-b border-gray-100 flex-shrink-0">
                  <div className="flex items-start gap-3">
                    <Building2
                      size={24}
                      className="text-blue-600 mt-1 flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-xl font-bold text-gray-800 mb-0">
                          {hotel.hotelName}
                        </h3>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            hotel.isActive ?? true
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {(hotel.isActive ?? true) ? "Active" : "Disabled"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Hotel Details */}
                <div className="p-4 space-y-3 flex-grow">
                  {/* Address */}
                  <div className="flex items-start gap-2">
                    <MapPin
                      size={16}
                      className="text-gray-400 mt-0.5 flex-shrink-0"
                    />
                    <p className="text-sm text-gray-600 leading-relaxed mb-0">
                      {hotel.address}
                    </p>
                  </div>

                  {/* Phone */}
                  <div className="flex items-center gap-2">
                    <Phone size={16} className="text-gray-400 flex-shrink-0" />
                    <p className="text-sm text-gray-600 mb-0">
                      {hotel.phoneNo}
                    </p>
                  </div>

                  {/* Location */}
                  <div className="flex items-center gap-2">
                    <MapPin size={16} className="text-gray-400 flex-shrink-0" />
                    <p className="text-sm text-gray-500 mb-0">
                      {hotel.city}, {hotel.state}, {hotel.country}
                    </p>
                  </div>

                  {/* Timeline Status */}
                  {(hotel.storeTimeline ||
                    hotel.pickupTimeline ||
                    hotel.deliveryTimeline) && (
                    <div className="bg-green-50 rounded-md p-2 mt-3">
                      <div className="flex items-center gap-2">
                        <Clock size={16} className="text-green-600" />
                        <p className="text-xs text-green-700 mb-0">
                          Timeline Available:
                          {hotel.storeTimeline && " Store"}
                          {hotel.pickupTimeline && " Pickup"}
                          {hotel.deliveryTimeline && " Delivery"}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Coordinates */}
                  <div className="bg-gray-50 rounded-md p-2 mt-3">
                    <p className="text-xs text-gray-500 mb-0">
                      Coordinates: {hotel.latitude.toFixed(6)},{" "}
                      {hotel.longitude.toFixed(6)}
                    </p>
                  </div>
                </div>

                {/* Hotel Actions */}
                <div className="p-4 pt-0 mt-auto flex-shrink-0 space-y-2">
                  <a
                    href={`https://maps.google.com/?q=${hotel.latitude},${hotel.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition text-sm"
                  >
                    <MapPin size={16} />
                    View on Map
                  </a>

                  {/* Timeline Button */}
                  {(hotel.storeTimeline ||
                    hotel.pickupTimeline ||
                    hotel.deliveryTimeline) && (
                    <button
                      onClick={() => openTimelineModal(hotel)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition text-sm"
                    >
                      <Eye size={16} />
                      View Timeline
                    </button>
                  )}

                  {/* Integrations Button */}
                  <button
                    onClick={() => openIntegrationsModal(hotel)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition text-sm"
                  >
                    <KeyRound size={16} />
                    Integrations
                  </button>

                  {/* Enable/Disable Toggle */}
                  {adminRole === "master_admin" && (
                    <button
                      onClick={() => handleToggleStatus(hotel)}
                      disabled={togglingStatusId === hotel._id}
                      className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-md transition text-sm text-white disabled:opacity-60 ${
                        (hotel.isActive ?? true)
                          ? "bg-orange-500 hover:bg-orange-600"
                          : "bg-emerald-600 hover:bg-emerald-700"
                      }`}
                    >
                      <Power size={16} />
                      {togglingStatusId === hotel._id
                        ? "Updating..."
                        : (hotel.isActive ?? true)
                          ? "Disable Location"
                          : "Enable Location"}
                    </button>
                  )}

                  {/* Edit/Delete Buttons */}
                  {isMasterAdmin ? (
                    <div className="grid grid-cols-2 gap-2 lg:grid-cols-1 xl:grid-cols-2">
                      <button
                        onClick={() => handleEdit(hotel)}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-md hover:bg-amber-600 transition text-sm"
                      >
                        <Pencil size={16} />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(hotel)}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition text-sm"
                      >
                        <Trash2 size={16} />
                        Delete
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleEdit(hotel)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-md hover:bg-amber-600 transition text-sm"
                    >
                      <Pencil size={16} />
                      View Details / Edit Timeline
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ================= PAGINATION ================= */}
      {filteredHotels.length > 0 && totalPages > 1 && (
        <div className="py-4 flex justify-center">
          <div className="flex gap-2 items-center">
            {/* Previous Button */}
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50 hover:bg-gray-300 transition"
            >
              Prev
            </button>

            {/* Page Numbers */}
            {(() => {
              const pages = [];
              const showEllipsis = totalPages > 7;

              if (!showEllipsis) {
                // Show all pages if 7 or fewer
                for (let i = 1; i <= totalPages; i++) {
                  pages.push(
                    <button
                      key={i}
                      onClick={() => setCurrentPage(i)}
                      className={`px-3 py-1 rounded transition ${
                        currentPage === i
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 hover:bg-gray-300"
                      }`}
                    >
                      {i}
                    </button>,
                  );
                }
              } else {
                // Smart pagination for many pages
                const startPage = Math.max(1, currentPage - 2);
                const endPage = Math.min(totalPages, currentPage + 2);

                // Always show first page
                if (startPage > 1) {
                  pages.push(
                    <button
                      key={1}
                      onClick={() => setCurrentPage(1)}
                      className={`px-3 py-1 rounded transition ${
                        currentPage === 1
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 hover:bg-gray-300"
                      }`}
                    >
                      1
                    </button>,
                  );

                  if (startPage > 2) {
                    pages.push(
                      <span key="ellipsis1" className="px-2 py-1 text-gray-500">
                        ...
                      </span>,
                    );
                  }
                }

                // Show pages around current page
                for (let i = startPage; i <= endPage; i++) {
                  if (i !== 1 && i !== totalPages) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`px-3 py-1 rounded transition ${
                          currentPage === i
                            ? "bg-blue-600 text-white"
                            : "bg-gray-200 hover:bg-gray-300"
                        }`}
                      >
                        {i}
                      </button>,
                    );
                  }
                }

                // Always show last page
                if (endPage < totalPages) {
                  if (endPage < totalPages - 1) {
                    pages.push(
                      <span key="ellipsis2" className="px-2 py-1 text-gray-500">
                        ...
                      </span>,
                    );
                  }

                  pages.push(
                    <button
                      key={totalPages}
                      onClick={() => setCurrentPage(totalPages)}
                      className={`px-3 py-1 rounded transition ${
                        currentPage === totalPages
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 hover:bg-gray-300"
                      }`}
                    >
                      {totalPages}
                    </button>,
                  );
                }
              }

              return pages;
            })()}

            {/* Next Button */}
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50 hover:bg-gray-300 transition"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* ================= ADD HOTEL MODAL ================= */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4"
          style={{ top: "0", left: "0", right: "0", bottom: "0" }}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={() => setShowModal(false)}
          />

          {/* Modal Container */}
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[95dvh] sm:max-h-[95vh] flex flex-col mx-auto">
            {/* Modal Header - Fixed */}
            <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4 rounded-t-lg flex justify-between items-center gap-3 flex-shrink-0">
              <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-0">
                {!isMasterAdmin
                  ? "Location Details"
                  : editingHotel
                    ? "Edit Location"
                    : "Add New Location"}
              </h2>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Content - Scrollable */}
            <div className="flex-1 overflow-y-auto">
              <form id="hotel-form" onSubmit={handleSubmit} className="p-4 sm:p-6">
                <div className="space-y-4">
                  {!isMasterAdmin && (
                    <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                      Location details are managed by the master admin. You can
                      only update the ASAP order delay and operating hours timeline.
                    </p>
                  )}
                  <fieldset disabled={!isMasterAdmin} className="min-w-0 space-y-4">
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Location Name *
                    </label>
                    <input
                      type="text"
                      value={hotelName}
                      onChange={(e) => setHotelName(e.target.value)}
                      placeholder="Enter Location name"
                      required
                      className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      value={phoneNo}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="Enter phone number"
                      required
                      className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>

                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                    <div>
                      <label
                        htmlFor="show-tip-modal"
                        className="text-sm font-semibold text-gray-800"
                      >
                        Show default tip selected
                      </label>
                      <p className="text-xs text-gray-500">
                        Enable this to prompt customers for tips during
                        checkout.
                      </p>
                    </div>
                    <div className="flex items-center">
                      <label className="relative h-6 w-11 cursor-pointer">
                        <input
                          id="show-tip-modal"
                          type="checkbox"
                          checked={defaultTip}
                          onChange={(e) => setdefaultTip(e.target.checked)}
                          className="peer sr-only"
                        />
                        <span className="absolute inset-0 rounded-full bg-gray-300 transition-colors peer-checked:bg-blue-600" />
                        <span className="absolute left-0.5 top-0.5 z-10 h-5 w-5 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-5" />
                      </label>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                    <div>
                      <label
                        htmlFor="cash-enabled-modal"
                        className="text-sm font-semibold text-gray-800"
                      >
                        Cash Enabled
                      </label>
                      <p className="text-xs text-gray-500">
                        Allow cash payments at this location.
                      </p>
                    </div>
                    <div className="flex items-center">
                      <label className="relative h-6 w-11 cursor-pointer">
                        <input
                          id="cash-enabled-modal"
                          type="checkbox"
                          checked={cashEnabled}
                          onChange={(e) => setCashEnabled(e.target.checked)}
                          className="peer sr-only"
                        />
                        <span className="absolute inset-0 rounded-full bg-gray-300 transition-colors peer-checked:bg-blue-600" />
                        <span className="absolute left-0.5 top-0.5 z-10 h-5 w-5 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-5" />
                      </label>
                    </div>
                  </div>

                  {/* Tip Settings Section */}
                  <div className="border border-gray-200 rounded-lg p-4 space-y-4">
                    <h3 className="text-sm font-semibold text-gray-800 border-b pb-2">
                      Tip Settings
                    </h3>

                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <label
                          htmlFor="delivery-tip-partner"
                          className="text-sm font-medium text-gray-700"
                        >
                          Delivery Tip to Partner
                        </label>
                        <p className="text-xs text-gray-500">
                          Enable to share delivery tips with partners.
                        </p>
                      </div>
                      <div className="flex items-center">
                        <label className="relative h-6 w-11 cursor-pointer">
                          <input
                            id="delivery-tip-partner"
                            type="checkbox"
                            checked={deliveryTipToPartner}
                            onChange={(e) =>
                              setDeliveryTipToPartner(e.target.checked)
                            }
                            className="peer sr-only"
                          />
                          <span className="absolute inset-0 rounded-full bg-gray-300 transition-colors peer-checked:bg-blue-600" />
                          <span className="absolute left-0.5 top-0.5 z-10 h-5 w-5 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-5" />
                        </label>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block mb-2 text-sm font-medium text-gray-700">
                          Base Tip Percent(%)
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          value={baseTipPercent}
                          onChange={(e) => setBaseTipPercent(e.target.value)}
                          placeholder="e.g., 15"
                          className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        />
                      </div>
                      <div>
                        <label className="block mb-2 text-sm font-medium text-gray-700">
                          Tip Gap Percent (%)
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          value={tipGapPercent}
                          onChange={(e) => setTipGapPercent(e.target.value)}
                          placeholder="e.g., 5"
                          className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Delivery Settings Section */}
                  <div className="border border-gray-200 rounded-lg p-4 space-y-4">
                    <h3 className="text-sm font-semibold text-gray-800 border-b pb-2">
                      Delivery Settings
                    </h3>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block mb-2 text-sm font-medium text-gray-700">
                          Within Radius (miles)
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          value={withinRadius}
                          onChange={(e) => setWithinRadius(e.target.value)}
                          placeholder="e.g., 10"
                          className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        />
                      </div>
                      <div>
                        <label className="block mb-2 text-sm font-medium text-gray-700">
                          Min Order Amount ($)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={minOrderAmount}
                          onChange={(e) => setMinOrderAmount(e.target.value)}
                          placeholder="e.g., 20"
                          className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        />
                      </div>
                      <div>
                        <label className="block mb-2 text-sm font-medium text-gray-700">
                          Delivery Amount ($)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={deliveryAmount}
                          onChange={(e) => setDeliveryAmount(e.target.value)}
                          placeholder="e.g., 5"
                          className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        />
                      </div>
                    </div>

                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Location *
                    </label>
                    {isMasterAdmin && (
                    <div className="border border-gray-300 rounded-md p-2">
                      <MapWithSearch
                        key={
                          editingHotel
                            ? `${editingHotel._id}-${mapResetKey}`
                            : "new"
                        }
                        onLocationChange={handleLocationChange}
                        editAddress={
                          editingHotel
                            ? {
                                address: fullMapAddress,
                                lat: latitude,
                                lng: longitude,
                              }
                            : null
                        }
                      />
                    </div>
                    )}
                    {fullMapAddress && (
                      <p className="text-sm text-gray-600 mt-2">
                        Selected: {fullMapAddress}
                      </p>
                    )}
                    {editingHotel && isMasterAdmin && (
                      <div className="mt-3 flex items-center gap-2">
                        <button
                          type="button"
                          onClick={handleResetLocation}
                          disabled={!hasLocationChanges}
                          className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Reset to saved location
                        </button>
                        <span className="text-xs text-gray-500">
                          This restores the last saved coordinates and address.
                        </span>
                      </div>
                    )}
                  </div>

                  {(city || state || country) && (
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                      <div className="flex items-start gap-2">
                        <MapPin size={16} className="text-blue-600 mt-0.5" />
                        <div className="text-sm text-blue-800">
                          <p className="font-medium mb-1">Location Details:</p>
                          <p>City: {city || "Not specified"}</p>
                          <p>State: {state || "Not specified"}</p>
                          <p>Country: {country || "Not specified"}</p>
                          {latitude && longitude && (
                            <p className="text-xs mt-1">
                              Coordinates: {latitude.toFixed(6)},{" "}
                              {longitude.toFixed(6)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  </fieldset>

                  {/* ASAP delay — editable by location admins too (outside the locked fieldset) */}
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      ASAP Order Delay (minutes)
                    </label>
                    <input
                      type="number"
                      step="1"
                      min="0"
                      value={asapDelay}
                      onChange={(e) => setAsapDelay(e.target.value)}
                      placeholder="e.g., 15"
                      className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Minutes added to current time for ASAP orders on the
                      online ordering page
                    </p>
                  </div>

                  {/* Timeline Section */}
                  <div>
                    <label className="block mb-3 text-sm font-medium text-gray-700">
                      Operating Hours Timeline
                    </label>

                    {/* Timeline Tabs */}
                    <div className="flex overflow-x-auto border-b border-gray-200 mb-4">
                      <button
                        type="button"
                        onClick={() => setActiveTimelineTab("store")}
                        className={`whitespace-nowrap px-4 py-2 text-sm font-medium border-b-2 ${
                          activeTimelineTab === "store"
                            ? "border-blue-600 text-blue-600"
                            : "border-transparent text-gray-500 hover:text-gray-700"
                        }`}
                      >
                        🏪 Store Hours
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveTimelineTab("pickup")}
                        className={`whitespace-nowrap px-4 py-2 text-sm font-medium border-b-2 ${
                          activeTimelineTab === "pickup"
                            ? "border-green-600 text-green-600"
                            : "border-transparent text-gray-500 hover:text-gray-700"
                        }`}
                      >
                        📦 Pickup Hours
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveTimelineTab("delivery")}
                        className={`whitespace-nowrap px-4 py-2 text-sm font-medium border-b-2 ${
                          activeTimelineTab === "delivery"
                            ? "border-purple-600 text-purple-600"
                            : "border-transparent text-gray-500 hover:text-gray-700"
                        }`}
                      >
                        🚚 Delivery Hours
                      </button>
                    </div>

                    {/* Timeline Content */}
                    <div className="max-h-64 overflow-y-auto border rounded-lg p-3">
                      {renderTimelineEditor()}
                    </div>
                  </div>
                </div>
              </form>
            </div>

            {/* Modal Footer - Fixed */}
            <div className="bg-gray-50 border-t border-gray-200 px-4 sm:px-6 py-4 rounded-b-lg flex flex-col-reverse sm:flex-row justify-end gap-3 flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="w-full sm:w-auto px-3 py-2 sm:px-5 sm:py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="hotel-form"
                className="w-full sm:w-auto px-3 py-2 sm:px-5 sm:py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
                disabled={formLoading}
              >
                {formLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin">⏳</span>
                    {editingHotel ? "Updating Location..." : "Adding Location..."}
                  </span>
                ) : !isMasterAdmin ? (
                  "Update Location"
                ) : editingHotel ? (
                  "Update Location"
                ) : (
                  "Add Location"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= TIMELINE VIEW MODAL ================= */}
      {showTimelineModal && selectedHotel && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4"
          style={{ top: "0", left: "0", right: "0", bottom: "0" }}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={() => setShowTimelineModal(false)}
          />

          {/* Modal Container */}
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[95dvh] sm:max-h-[95vh] flex flex-col mx-auto">
            {/* Modal Header - Fixed */}
            <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4 rounded-t-lg flex justify-between items-start gap-3 flex-shrink-0">
              <div className="flex min-w-0 items-start gap-3">
                <Clock size={22} className="text-blue-600 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-0 break-words">
                    {selectedHotel.hotelName} - Timeline
                  </h2>
                  <p className="text-xs sm:text-sm text-gray-500 break-words">
                    {selectedHotel.address}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowTimelineModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              <div className="space-y-6">
                {/* Store Timeline */}
                {renderTimelineSection(
                  "🏪 Store Operating Hours",
                  selectedHotel.storeTimeline,
                  "text-blue-600",
                )}

                {/* Pickup Timeline */}
                {renderTimelineSection(
                  "📦 Pickup Hours",
                  selectedHotel.pickupTimeline,
                  "text-green-600",
                )}

                {/* Delivery Timeline */}
                {renderTimelineSection(
                  "🚚 Delivery Hours",
                  selectedHotel.deliveryTimeline,
                  "text-purple-600",
                )}

                {/* No Timeline Available */}
                {!selectedHotel.storeTimeline &&
                  !selectedHotel.pickupTimeline &&
                  !selectedHotel.deliveryTimeline && (
                    <div className="text-center py-8">
                      <Clock size={48} className="mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-500">
                        No timeline information available for this Location.
                      </p>
                    </div>
                  )}
              </div>
            </div>

            {/* Modal Footer - Fixed */}
            <div className="bg-gray-50 border-t border-gray-200 px-4 sm:px-6 py-4 rounded-b-lg flex justify-end flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowTimelineModal(false)}
                className="w-full sm:w-auto px-5 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= LOCATION INTEGRATIONS MODAL ================= */}
      {showIntegrationsModal && integrationsHotel && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4"
          style={{ top: "0", left: "0", right: "0", bottom: "0" }}
        >
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowIntegrationsModal(false)}
          />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[95dvh] sm:max-h-[90vh] flex flex-col mx-auto">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4 rounded-t-lg flex justify-between items-start gap-3 flex-shrink-0">
              <div className="flex min-w-0 items-start gap-3">
                <KeyRound size={22} className="text-indigo-600 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-0 break-words">
                    Location Integrations — {integrationsHotel.hotelName}
                  </h2>
                  <p className="text-xs sm:text-sm text-gray-500 break-words">
                    Uber Direct, DoorDash, and Google Places credentials for
                    this location
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowIntegrationsModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
              >
                <X size={20} />
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto flex-1 p-4 sm:p-6 space-y-6">
              {integrationsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
                </div>
              ) : (
                LOCATION_PROVIDERS.map((provider) => {
                  const status = integrationsStatusMap[provider.key];
                  const form = integrationsForms[provider.key] || {
                    accountEmail: "",
                    credentials: {},
                  };
                  return (
                    <div
                      key={provider.key}
                      className="bg-gray-50 border border-gray-200 rounded-lg p-4 sm:p-5"
                    >
                      <div className="mb-3 flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {provider.title}
                        </h3>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${status?.configured ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}
                        >
                          {status?.configured ? "Configured" : "Not Configured"}
                        </span>
                      </div>

                      <div className="flex flex-wrap gap-2 mb-4">
                        <a
                          href={provider.signupUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-indigo-600 text-white text-xs hover:bg-indigo-700"
                        >
                          Open Provider <ExternalLink size={12} />
                        </a>
                        <a
                          href={provider.docsUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-gray-300 text-gray-700 text-xs hover:bg-gray-50"
                        >
                          Docs <ExternalLink size={12} />
                        </a>
                      </div>

                      <div className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Account Email
                        </label>
                        <input
                          type="email"
                          value={form.accountEmail}
                          onChange={(e) =>
                            setIntegrationsForms((prev) => ({
                              ...prev,
                              [provider.key]: {
                                ...prev[provider.key],
                                accountEmail: e.target.value,
                              },
                            }))
                          }
                          placeholder={provider.accountHelp}
                          disabled={!isMasterAdmin}
                          className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:text-gray-500"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                        {provider.fields.map((field) => (
                          <div key={field.key}>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              {field.label}
                              {field.required ? " *" : ""}
                            </label>
                            <input
                              type="password"
                              value={form.credentials[field.key] || ""}
                              onChange={(e) =>
                                setIntegrationsForms((prev) => ({
                                  ...prev,
                                  [provider.key]: {
                                    ...prev[provider.key],
                                    credentials: {
                                      ...prev[provider.key].credentials,
                                      [field.key]: e.target.value,
                                    },
                                  },
                                }))
                              }
                              placeholder={
                                status?.env?.[field.key]
                                  ? `Existing: ${status.env[field.key]}`
                                  : "Paste value"
                              }
                              disabled={!isMasterAdmin}
                              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:text-gray-500"
                            />
                          </div>
                        ))}
                      </div>

                      {status?.updatedAt && (
                        <p className="text-xs text-gray-500 mb-3">
                          Last updated:{" "}
                          {new Date(status.updatedAt).toLocaleString()}
                        </p>
                      )}

                      {isMasterAdmin && (
                      <button
                        onClick={() => saveLocationIntegration(provider.key)}
                        disabled={integrationsSaving === provider.key}
                        className="inline-flex w-full sm:w-auto items-center justify-center gap-2 px-4 py-2 rounded bg-gray-900 text-white text-sm hover:bg-gray-800 disabled:opacity-50"
                      >
                        <Save size={14} />
                        {integrationsSaving === provider.key
                          ? "Saving..."
                          : "Save Credentials"}
                      </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HotelManagement;
